import { Card, Field, Header, Select, Space, Text } from '@procurify/ui'

import { PrimaryCategorySelect } from '@webapp/core/components/PrimaryCategorySelect'
import { PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

export interface IReportAccessHeaderCardProps {
  selectedPrimaryCategory: PrimaryCategory | undefined
  isAllPrimaryCategoriesSelected: boolean
  onSelectPrimaryCategory: (primaryCategory: PrimaryCategory) => void
  onSelectAllPrimaryCategories: () => void
}

/**
 * Presentational Feature Set subcomponent — page title, descriptive subtext
 * and the labelled PrimaryCategorySelect with the sticky "ALL" option pinned
 * below a divider (BHV-003). ALL mode is explicit Feature Set state — the
 * display-only option below never leaves this component.
 */
export const ReportAccessHeaderCard = ({
  selectedPrimaryCategory,
  isAllPrimaryCategoriesSelected,
  onSelectPrimaryCategory,
  onSelectAllPrimaryCategories,
}: IReportAccessHeaderCardProps) => {
  const intl = useProcurifyIntl()

  // Display-only control value while ALL mode is active (mode state itself
  // lives in the Feature Set — the Angular `{ id: -1 }` sentinel is not state)
  const allPrimaryCategoriesDisplayOption = new PrimaryCategory({
    id: -1,
    name: intl.formatMessage(messages.allOptionLabel),
  })

  return (
    <Card>
      <Card.Content>
        <Space direction='horizontal' justify='space-between' align='start'>
          <Space.Item grow={1}>
            <Space size='small'>
              <Header as='h3'>{intl.formatMessage(messages.title)}</Header>
              <Text>{intl.formatMessage(messages.description)}</Text>
            </Space>
          </Space.Item>
          <Space.Item minWidth={340}>
            <Field
              label={intl.formatMessage(messages.primaryCategorySelectLabel)}
            >
              <PrimaryCategorySelect
                value={
                  isAllPrimaryCategoriesSelected
                    ? allPrimaryCategoriesDisplayOption
                    : (selectedPrimaryCategory ?? null)
                }
                onChange={(option) => {
                  if (option) onSelectPrimaryCategory(option)
                }}
                stickies={({ closeMenu }) => [
                  <Select.StickyOption
                    key='all-primary-categories'
                    onClick={() => {
                      closeMenu()
                      onSelectAllPrimaryCategories()
                    }}
                  >
                    {intl.formatMessage(messages.allOptionLabel)}
                  </Select.StickyOption>,
                ]}
              />
            </Field>
          </Space.Item>
        </Space>
      </Card.Content>
    </Card>
  )
}

ReportAccessHeaderCard.displayName = 'ReportAccessHeaderCard'
